// Shared micro-components used across steps
import { G, GL, GB, BORDER, LIGHT, MID, BLACK, BG } from "../constants";

export function Tag({ children }) {
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: GL, color: G, border: `1px solid ${GB}` }}>{children}</span>;
}

export function SevBadge({ level }) {
  const hi = level === "high";
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: hi ? "#fef2f2" : "#fffbeb", color: hi ? "#dc2626" : "#d97706", textTransform: "uppercase", letterSpacing: "0.4px" }}>{level}</span>;
}

export function Ring({ score, size = 44 }) {
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  const d = (score / 100) * c;
  const col = score >= 75 ? G : score >= 50 ? "#d97706" : "#dc2626";
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={BORDER} strokeWidth={3.5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={3.5}
        strokeDasharray={`${d} ${c}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: "stroke-dasharray 0.5s ease" }} />
      <text x={size/2} y={size/2+4} textAnchor="middle" fill={col} fontSize={size > 40 ? 11 : 9} fontWeight="700" fontFamily="inherit">{score}</text>
    </svg>
  );
}

export function BigRing({ score, animated, trackColor, size = 100, strokeWidth = 8 }) {
  const r   = (size / 2) - strokeWidth;
  const cx  = size / 2;
  const cy  = size / 2;
  const c   = 2 * Math.PI * r;
  const col = score >= 70 ? G : score >= 50 ? "#d97706" : "#dc2626";
  const pct = animated ? score : 0;
  const dash = (pct / 100) * c;
  return (
    <svg width={size} height={size} style={{ display: "block", margin: "0 auto 12px" }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={trackColor || BORDER} strokeWidth={strokeWidth} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: "stroke-dasharray 1.2s ease" }} />
      <text x={cx} y={cy - 4} textAnchor="middle" fill={col} fontSize={24} fontWeight="800" fontFamily="inherit">{score}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="#9ca3af" fontSize={11} fontFamily="inherit">/ 100</text>
    </svg>
  );
}

export function SourceLogo({ src, size = 18 }) {
  const logos = {
    reddit:    (<svg width={size} height={size} viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="10" fill="#ff4500"/>
                  <circle cx="10" cy="11" r="4.5" fill="#fff"/>
                  <circle cx="7.8" cy="10.5" r=".85" fill="#ff4500"/>
                  <circle cx="12.2" cy="10.5" r=".85" fill="#ff4500"/>
                  <path d="M8.2 13c.5.5 1.1.7 1.8.7s1.3-.2 1.8-.7" stroke="#ff4500" strokeWidth=".7" fill="none" strokeLinecap="round"/>
                  <circle cx="14.2" cy="6.8" r="1.4" fill="#fff"/>
                  <circle cx="14.2" cy="6.8" r=".65" fill="#ff4500"/>
                  <path d="M11.2 7.8c.4-1.1 1.6-1.7 3-1.4" stroke="#ff4500" strokeWidth=".6" fill="none" strokeLinecap="round"/>
                </svg>),
    youtube:   (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#ff0000"/><polygon points="8,6 8,14 15,10" fill="#fff"/></svg>),
    tiktok:    (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#010101"/><path d="M12 4h-2v8.5a2 2 0 1 1-2-2V8.4a4.1 4.1 0 1 0 4 4.1V8.3A5.5 5.5 0 0 0 15 9.5V7.6A3.5 3.5 0 0 1 12 4z" fill="#fff"/></svg>),
    pinterest: (<svg width={size} height={size} viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#e60023"/><path d="M10 3.5C6.4 3.5 3.5 6.4 3.5 10c0 2.7 1.6 5 4 6.1.1-.5.1-1.3.2-2l1.2-5.1s-.3-.6-.3-1.5c0-1.4.8-2.5 2-2.5 1 0 1.4.7 1.4 1.6 0 1-.6 2.5-.9 3.9-.3 1.1.5 2 1.6 2 2 0 3.3-2.5 3.3-5.5 0-2.3-1.5-4-4.3-4-3.1 0-5 2.3-5 4.9 0 .9.3 1.5.7 2 .1.1.1.2.1.4l-.3 1c-.1.3-.3.4-.5.2-1.5-.6-2.2-2.3-2.2-4.1 0-3 2.5-6.5 7.5-6.5 4 0 6.6 3 6.6 6.1 0 4.1-2.3 7.2-5.6 7.2-1.1 0-2.1-.6-2.5-1.3l-.7 2.8c-.3.9-.8 1.8-1.2 2.4.8.3 1.7.4 2.6.4 3.6 0 6.5-2.9 6.5-6.5s-2.9-6.5-6.5-6.5z" fill="#fff"/></svg>),
    amazon:    (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#232f3e"/><text x="10" y="14" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#f90">&#9733;</text></svg>),
    depop:     (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#ff2300"/><text x="10" y="14" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#fff">d</text></svg>),
    gsc:       (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#fff" stroke="#dde"/><text x="10" y="14" textAnchor="middle" fontSize="8" fontWeight="800" fill="#4285f4">GSC</text></svg>),
    ga4:       (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#fff" stroke="#dde"/><text x="10" y="14" textAnchor="middle" fontSize="8" fontWeight="800" fill="#34a853">GA4</text></svg>),
    sem:       (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#fff" stroke="#dde"/><text x="10" y="14" textAnchor="middle" fontSize="8" fontWeight="800" fill="#f59e0b">ADS</text></svg>),
    trends:    (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#fff" stroke="#dde"/><text x="10" y="14" textAnchor="middle" fontSize="7" fontWeight="800" fill="#ea4335">TRD</text></svg>),
    manual:    (<svg width={size} height={size} viewBox="0 0 20 20"><rect width="20" height="20" rx="4" fill="#fff" stroke="#dde"/><text x="10" y="14" textAnchor="middle" fontSize="7" fontWeight="800" fill="#6c63ff">MAN</text></svg>),
  };
  return logos[src] || <span style={{ width: size, height: size, borderRadius: 4, background: "#999", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 800, color: "#fff" }}>?</span>;
}

export function IntentBadge({ intent }) {
  const cfg = {
    high:      { bg: "#f0faf4", color: "#1a7a3c", label: "High intent"  },
    med:       { bg: "#fffbeb", color: "#d97706", label: "Med intent"   },
    seasonal:  { bg: "#fff7ed", color: "#c2410c", label: "Seasonal"     },
    evergreen: { bg: "#f3f4f6", color: "#6b7280", label: "Evergreen"    },
    low:       { bg: "#f3f4f6", color: "#6b7280", label: "Low"          },
  };
  const c = cfg[intent] || cfg.med;
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: c.bg, color: c.color, textTransform: "uppercase", letterSpacing: "0.3px", whiteSpace: "nowrap" }}>{c.label}</span>;
}

export function ProgressBar({ before, after, max, color }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 10, color: LIGHT, width: 32 }}>Before</span>
        <div style={{ flex: 1, height: 4, background: "#e5e7eb", borderRadius: 2 }}>
          <div style={{ width: `${Math.min((before / max) * 100, 100)}%`, height: "100%", background: "#d1d5db", borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 10, color: LIGHT, width: 32 }}>After</span>
        <div style={{ flex: 1, height: 4, background: "#e5e7eb", borderRadius: 2 }}>
          <div style={{ width: `${Math.min((after / max) * 100, 100)}%`, height: "100%", background: color, borderRadius: 2, transition: "width 0.8s ease" }} />
        </div>
      </div>
    </div>
  );
}

export const Label = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 700, color: LIGHT, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 10 }}>{children}</div>
);

export function renderWithExclusions(text, excludedPhrases) {
  if (!excludedPhrases || excludedPhrases.length === 0) return text;
  const parts = [];
  let remaining = text;
  const sorted = [...excludedPhrases].sort((a, b) => b.length - a.length);
  while (remaining.length > 0) {
    let earliest = -1, earliestPhrase = null, earliestLen = 0;
    for (const phrase of sorted) {
      const idx = remaining.toLowerCase().indexOf(phrase.toLowerCase());
      if (idx !== -1 && (earliest === -1 || idx < earliest)) {
        earliest = idx;
        earliestPhrase = phrase;
        earliestLen = phrase.length;
      }
    }
    if (earliest === -1) { parts.push({ type: "normal", text: remaining }); break; }
    if (earliest > 0) parts.push({ type: "normal", text: remaining.slice(0, earliest) });
    parts.push({ type: "excluded", text: remaining.slice(earliest, earliest + earliestLen) });
    remaining = remaining.slice(earliest + earliestLen);
  }
  return parts.map((part, i) =>
    part.type === "excluded"
      ? <span key={i} style={{ textDecoration: "line-through", color: "#dc2626", background: "#fef2f2", borderRadius: 2, padding: "0 1px" }}>{part.text}</span>
      : <span key={i}>{part.text}</span>
  );
}
